import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import getSymbolFromCurrency from "currency-symbol-map";

import { useAppSelector, useAppDispatch } from "store/index";
import { handleUpdateFields } from "store/liveOps/tournaments/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

import InnerInput from "components/inputs/InnerInput/InnerInput";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Checkbox from "components/inputs/Checkbox/Checkbox";

const RULES_FIELDS = [
  "depositMinSum",
  "betsMinCount",
  "userCategoryId",
  "loyaltyPoints",
  "rankingPoints",
  "coins",
  "emailConfirmed",
  "phoneConfirmed",
  "kycConfirmed",
];

interface Props {
  activeCurrency: string;
  setActiveCurrency: (e: string) => void;
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  clearFieldsErrors: (field: string[]) => void;
  readOnly?: boolean;
}

const TournamentsRulesTab: React.FC<Props> = ({
  activeCurrency,
  setActiveCurrency,
  fieldErrors,
  clearFieldError,
  clearFieldsErrors,
  readOnly,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const currenciesList = useAppSelector((store) => store.settings.currencies);
  const payload = useAppSelector((store) => store.tournaments.payload);
  const currentItem = payload?.activationRules?.filter(
    (el) => el?.currency === activeCurrency,
  )[0];

  const [options, setOptions] = useState<{ value: string; label: string }[]>(
    [],
  );

  const getOptions = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.playerCategoriesList}?limit=1000&offset=0`,
      );
      const array = result.data.items?.map((el: any) => {
        return {
          value: el.id,
          label: el.title,
        };
      });
      setOptions(array);
    } catch {
      console.log("getOptions error");
    }
  };

  useEffect(() => {
    getOptions();
  }, []);

   useEffect(() => {
     clearFieldsErrors(RULES_FIELDS);
   }, [activeCurrency]);

  const handleDataRules = (key: string, value: string | number) => {
    if (currentItem && payload?.activationRules) {
      // @ts-ignore
      dispatch(
        handleUpdateFields({
          ...payload,
          activationRules: [
            ...payload?.activationRules?.filter(
              (el) => el?.currency !== activeCurrency,
            ),
            {
              ...currentItem,
              currency: activeCurrency,
              [key]: value,
            },
          ],
        }),
      );
    } else {
      const config = payload?.activationRules
        ? payload?.activationRules?.filter(
            (el) => el?.currency !== activeCurrency,
          )
        : [];
      dispatch(
        // @ts-ignore
        handleUpdateFields({
          ...payload,
          activationRules: [
            // @ts-ignore
            ...config,
            // @ts-ignore
            {
              currency: activeCurrency,
              [key]: value,
            },
          ],
        }),
      );
    }
  };

  return (
    <div className="tournament-rules">
      <div className="game-modal-tabs bonus-currencies-tabs small">
        {currenciesList?.map((el) => (
          <div
            className={`tab-item ${activeCurrency === el ? "active" : ""}`}
            key={el}
            onClick={() => {
              setActiveCurrency(el);
            }}
          >
            <div className="currency-tab-item">
              <div className="currency">{getSymbolFromCurrency(el)}</div> {el}
            </div>
          </div>
        ))}
      </div>
      <div className="tournament-rules-form">
        <div className="row">
          <div className="bonus-form-box--item">
            <InnerInput
              value={currentItem?.depositMinSum?.toString() ?? ""}
              placeholder={t("depositMinSum")}
              label={t("depositMinSum")}
              type="number"
              id="activation-depositMinSum"
              onChange={(e) => {
                handleDataRules("depositMinSum", e);
                clearFieldError("depositMinSum");
              }}
              hasError={Boolean(fieldErrors?.["depositMinSum"])}
              disabled={readOnly}
            />
          </div>
          <div className="bonus-form-box--item">
            <InnerInput
              value={currentItem?.betsMinCount?.toString() ?? ""}
              placeholder={t("betsMinCount")}
              label={t("betsMinCount")}
              type="number"
              id="activation-betsMinCount"
              onChange={(e) => {
                handleDataRules("betsMinCount", e);
                clearFieldError("betsMinCount");
              }}
              hasError={Boolean(fieldErrors?.["betsMinCount"])}
              disabled={readOnly}
            />
          </div>
          <div className="bonus-form-box--item">
            <DefaultDropdown
              value={currentItem?.userCategoryId}
              placeholder={t("userCategory")}
              label={t("userCategory")}
              options={options}
              disabled={options.length === 0 || readOnly}
              onSelect={(e) => {
                handleDataRules("userCategoryId", e as string);
                clearFieldError("userCategoryId");
              }}
              hasError={Boolean(fieldErrors?.["userCategoryId"])}
            />
          </div>
        </div>
        <div className="row">
          <div className="bonus-form-box--item">
            <Checkbox
              value={!!currentItem?.emailConfirmed}
              txt={t("emailConfirmed")}
              id="activation-emailConfirmed"
              onCheck={() => {
                handleDataRules(
                  "emailConfirmed",
                  !!currentItem?.emailConfirmed ? 0 : 1,
                );
                clearFieldError("emailConfirmed");
              }}
              hasError={Boolean(fieldErrors?.["emailConfirmed"])}
              disabled={readOnly}
            />
          </div>
          <div className="bonus-form-box--item">
            <Checkbox
              value={!!currentItem?.phoneConfirmed}
              txt={t("phoneConfirmed")}
              id="activation-phoneConfirmed"
              onCheck={() => {
                handleDataRules(
                  "phoneConfirmed",
                  !!currentItem?.phoneConfirmed ? 0 : 1,
                );
                clearFieldError("phoneConfirmed");
              }}
              hasError={Boolean(fieldErrors?.["phoneConfirmed"])}
              disabled={readOnly}
            />
          </div>
          <div className="bonus-form-box--item">
            <Checkbox
              value={!!currentItem?.kycConfirmed}
              txt={t("kycConfirmed")}
              onCheck={() => {
                handleDataRules(
                  "kycConfirmed",
                  !!currentItem?.kycConfirmed ? 0 : 1,
                );
                clearFieldError("kycConfirmed");
              }}
              id="activation-kycConfirmed"
              hasError={Boolean(fieldErrors?.["kycConfirmed"])}
              disabled={readOnly}
            />
          </div>
        </div>
        <div className="row">
          <div className="bonus-form-box--item">
            <InnerInput
              value={currentItem?.loyaltyPoints?.toString() ?? ""}
              placeholder={t("loyaltyPoints")}
              label={t("loyaltyPoints")}
              type="number"
              id="activation-loyaltyPoints"
              onChange={(e) => {
                handleDataRules("loyaltyPoints", e);
                clearFieldError("loyaltyPoints");
              }}
              hasError={Boolean(fieldErrors?.["loyaltyPoints"])}
              disabled={readOnly}
            />
          </div>
          <div className="bonus-form-box--item">
            <InnerInput
              value={currentItem?.rankingPoints?.toString() ?? ""}
              placeholder={t("rankingPoints")}
              label={t("rankingPoints")}
              type="number"
              id="activation-rankingPoints"
              onChange={(e) => {
                handleDataRules("rankingPoints", e);
                clearFieldError("rankingPoints");
              }}
              hasError={Boolean(fieldErrors?.["rankingPoints"])}
              disabled={readOnly}
            />
          </div>
          <div className="bonus-form-box--item">
            <InnerInput
              value={currentItem?.coins?.toString() ?? ""}
              placeholder={t("coins")}
              label={t("coins")}
              type="number"
              id="activation-coins"
              onChange={(e) => {
                handleDataRules("coins", e);
                clearFieldError("coins");
              }}
              hasError={Boolean(fieldErrors?.["coins"])}
              disabled={readOnly}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentsRulesTab;
